/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {Controller} from 'react-hook-form'
import {
    FormControl,
    FormLabel,
    FormErrorMessage,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    InputLeftElement,
    Select,
    Checkbox,
    Radio,
    RadioGroup,
    VStack,
    Text,
    Box,
    Flex,
    Tooltip,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Switch} from '@chakra-ui/react'
import {Textarea, useTheme} from '@chakra-ui/react'
import {VisibilityIcon, VisibilityOffIcon, CalendarIcon, ErrorIcon, ChevronDownType2, InfoCircle} from '../../components/icons'
import WebFloatingSelect from '../web-floating-select'
import {useIntl} from 'react-intl'
import {sendViewErrorEvent} from '../../utils/gtm-utils'
import {flushSync} from 'react-dom'

const formatDateInput = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 8)
    return [digits.slice(0, 4), digits.slice(4, 6), digits.slice(6, 8)]
        .filter(Boolean)
        .join('-')
}

const getISODate = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    if (!match) return ''

    const [, year, month, day] = match
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))
    const isValidDate =
        date.getUTCFullYear() === Number(year) &&
        date.getUTCMonth() === Number(month) - 1 &&
        date.getUTCDate() === Number(day)

    return isValidDate ? `${year}-${month}-${day}` : ''
}

const Field = ({
    name,
    label,
    formLabel,
    type = 'text',
    options = [],
    rules = {},
    error,
    placeholder,
    inputProps,
    labelProps,
    control,
    autoComplete,
    defaultValue,
    helpText,
    children,
    inputRef,
    hideLabel = false,
    isResetPassword = false,
    formControlProps,
    variant,
    rows = 4,
    maxLength,
    showCharacterCount = false,
    showPasswordToggle = true,
    onClick,
    isLoginPage = false,
    secondaryLabel,
    webHelperText,
    webHelperIcon: WebHelperIcon,
    webHelperStyle,
    hideRequiredIndicator = false,
    hideErrorMessage = false,
    errorMessageProps,
    showSelectedCheckmark = false,
    nativeSelect = false,
    displayValueAsLabel = false,
    webTooltip,
    webTooltipPlacement = 'top',
    webTooltipButtonProps,
    radioGroupProps,
    switchLabels,
    switchProps,
    iconProps,
    calendarIconPosition,
    dataPrivate = false
}) => {
    const intl = useIntl()
    const theme = useTheme()
    const [hidePassword, setHidePassword] = useState(true)
    const [isDateFocused, setIsDateFocused] = useState(false)
    const [isDatePickerMode, setIsDatePickerMode] = useState(false)
    const [dateText, setDateText] = useState('')
    const [isSelectFocused, setIsSelectFocused] = useState(false)
    const selectChangedRef = useRef(false)
    const dateInputRef = useRef(null)
    const dateTextRef = useRef('')
    const PasswordIcon = isLoginPage
        ? hidePassword
            ? VisibilityIcon
            : VisibilityOffIcon
        : hidePassword
        ? VisibilityOffIcon
        : VisibilityIcon
    const passwordIconLabel = hidePassword
        ? intl.formatMessage({
              id: 'field.password.assistive_msg.show_password',
              defaultMessage: 'Show password'
          })
        : intl.formatMessage({
              id: 'field.password.assistive_msg.hide_password',
              defaultMessage: 'Hide password'
          })

    const finalPlaceholder =
        variant === 'floating' || variant === 'floatingProfileForm' ? ' ' : placeholder

    const isWebVariant = variant === 'webFloatingField' || variant === 'webFloatingFieldTextArea'
    const webPlaceholder = isWebVariant ? ' ' : finalPlaceholder

    // The native date input renders its segments in the browser's locale, never the
    // storefront's, so dates are typed into a masked text input instead.
    const isMaskedDate = type === 'date'
    const datePlaceholder = intl.formatMessage({
        id: 'field.date.placeholder',
        defaultMessage: 'YYYY-MM-DD'
    })
    const isLeftIcon = calendarIconPosition === 'left'

    // Masked dates only reach the form as an ISO string (or '' while incomplete), so an
    // impossible entry like 2024-77-48 would otherwise be silently dropped and pass every
    // rule. Validate the raw text here so the field always reports an error instead.
    const dateValidationRules = isMaskedDate
        ? {
              ...rules,
              validate: {
                  ...(typeof rules.validate === 'function'
                      ? {fieldRules: rules.validate}
                      : rules.validate),
                  validDate: () =>
                      !dateTextRef.current ||
                      Boolean(getISODate(dateTextRef.current)) ||
                      intl.formatMessage({
                          id: 'field.date.error.invalid_format',
                          defaultMessage: 'Please enter a valid date'
                      })
              }
          }
        : rules

    useEffect(() => {
        if (error?.message && !hideErrorMessage) sendViewErrorEvent(error.message)
    }, [error?.message, hideErrorMessage])

    if (isWebVariant && type === 'select' && !nativeSelect) {
        return (
            <WebFloatingSelect
                name={name}
                label={label}
                secondaryLabel={secondaryLabel}
                options={options}
                rules={rules}
                error={error}
                control={control}
                defaultValue={defaultValue}
                webHelperText={webHelperText}
                webHelperIcon={WebHelperIcon}
                webHelperStyle={webHelperStyle}
                hideRequiredIndicator={hideRequiredIndicator}
                formControlProps={formControlProps}
                showSelectedCheckmark={showSelectedCheckmark}
                displayValueAsLabel={displayValueAsLabel}
                onClick={onClick}
                autoComplete={autoComplete}
                dataPrivate={dataPrivate}
            />
        )
    }

    return (
        <Controller
            name={name}
            control={control}
            rules={dateValidationRules}
            defaultValue={defaultValue}
            render={({field: {onChange, value, ref}}) => {
                const getInputType = () => {
                    if (type === 'password') {
                        return hidePassword ? 'password' : 'text'
                    }
                    
                    if (isMaskedDate) {
                        return isDatePickerMode ? 'date' : 'text'
                    }

                    return type
                }

                const inputType = getInputType()

                const _inputProps =
                    typeof inputProps === 'function' ? inputProps({value, onChange}) : inputProps

                const handleInputChange = (event) => {
                    if (!isMaskedDate) {
                        onChange(event)
                        return
                    }

                    if (isDatePickerMode) {
                        setDateText(event.target.value)
                        dateTextRef.current = event.target.value
                        onChange(event)
                        return
                    }

                    const displayValue = formatDateInput(event.target.value)
                    setDateText(displayValue)
                    dateTextRef.current = displayValue
                    onChange(getISODate(displayValue))
                }

                const inputValue =
                    isMaskedDate && !isDatePickerMode ? dateText || value || '' : value || ''



                const labelElement =
                    !hideLabel &&
                    !['checkbox', 'radio', 'hidden'].includes(type) &&
                    (formLabel || (
                        <FormLabel {...labelProps}>
                            {label}
                            {isWebVariant && rules?.required && !hideRequiredIndicator && (
                                <span className="web-field-suffix"> *</span>
                            )}
                            {isWebVariant && secondaryLabel && (
                                <span className="web-field-optional"> {secondaryLabel}</span>
                            )}
                        </FormLabel>
                    ))

                return (
                    <FormControl
                        id={name}
                        isInvalid={error}
                        variant={variant}
                        {...formControlProps}
                        sx={{
                            ...(type === 'date' &&
                                calendarIconPosition === 'left' && {
                                    '& label': {
                                        left: isDateFocused ? '9px !important' : '36px !important'
                                    }
                                })
                        }}
                    >
                        {variant !== 'floating' &&
                            variant !== 'floatingProfileForm' &&
                            !isWebVariant &&
                            labelElement}

                        <InputGroup>
                            {[
                                'text',
                                'password',
                                'email',
                                'phone',
                                'tel',
                                'number',
                                'date'
                            ].includes(type) && (
                                <Input
                                    ref={(node) => {
                                        ref(node)
                                        if (inputRef) inputRef.current = node
                                        if (type === 'date') {
                                            dateInputRef.current = node
                                        }
                                    }}
                                    onChange={handleInputChange}
                                    onClick={onClick}
                                    onFocus={(e) => {
                                        if (type === 'date' && !isDateFocused) {
                                            setIsDateFocused(true)
                                        }
                                        _inputProps?.onFocus?.(e)
                                    }}
                                    onBlur={(e) => {
                                        if (type === 'date') {
                                            setIsDateFocused(false)
                                            setIsDatePickerMode(false)
                                        }
                                        _inputProps?.onBlur?.(e)
                                    }}
                                    value={inputValue}
                                    type={inputType}
                                    placeholder={
                                        isMaskedDate && !isDatePickerMode && isDateFocused
                                            ? datePlaceholder
                                            : webPlaceholder
                                    }
                                    autoComplete={autoComplete}
                                    {...(!isWebVariant && {borderColor: 'gray.700'})}
                                    {..._inputProps}
                                    {...(isMaskedDate &&
                                        !isDatePickerMode && {
                                            onKeyDown: undefined,
                                            onPaste: undefined,
                                            inputMode: 'numeric',
                                            maxLength: 10
                                        })}
                                    {...(isWebVariant && {variant: 'webTextInput'})}
                                    {...(!isLoginPage
                                        ? {textStyle: 'bodyMdRegular'}
                                        : {fontSize: _inputProps?.fontSize})}
                                    sx={{
                                        ..._inputProps?.sx,
                                        ...(isWebVariant && webTooltip && {paddingRight: '36px'}),
                                        ...(type === 'date' && {
                                            '&::-webkit-date-and-time-value': {
                                                textAlign: 'left'
                                            }
                                        }),
                                        ...(isMaskedDate
                                            ? {
                                                  '&::-webkit-calendar-picker-indicator': {
                                                      display: 'none !important',
                                                      opacity: '0 !important',
                                                      width: '0 !important',
                                                      height: '0 !important'
                                                  }
                                              }
                                            : {})
                                    }}
                                />
                            )}

                            {type === 'hidden' && (
                                <input
                                    ref={ref}
                                    onChange={onChange}
                                    value={value || ''}
                                    type="hidden"
                                    {..._inputProps}
                                />
                            )}

                            {type === 'password' && showPasswordToggle && (
                                <InputRightElement w="auto" h="100%" right="6px">
                                    <IconButton
                                        size="xs"
                                        {...iconProps}
                                        variant="ghosted"
                                        aria-label={passwordIconLabel}
                                        icon={<PasswordIcon color="black" w={4} h={4} />}
                                        onClick={() => setHidePassword(!hidePassword)}
                                    />
                                </InputRightElement>
                            )}

                            {isWebVariant && webTooltip && (
                                <InputRightElement
                                    w="auto"
                                    h="100%"
                                    right="8px"
                                    top="0"
                                    display="flex"
                                    alignItems="center"
                                >
                                    <Tooltip
                                        hasArrow={false}
                                        placement={webTooltipPlacement}
                                        variant="info"
                                        label={webTooltip}
                                    >
                                        <IconButton
                                            variant="unstyled"
                                            display="flex"
                                            minW="auto"
                                            h="20px"
                                            width="20px"
                                            aria-label="More information"
                                            icon={<InfoCircle width="20px" height="20px" />}
                                            {...webTooltipButtonProps}
                                        />
                                    </Tooltip>
                                </InputRightElement>
                            )}

                            {isMaskedDate && !isLeftIcon && (
                                <InputRightElement
                                    w="auto"
                                    h={'100%'}
                                    right="8px"
                                >
                                    <IconButton
                                        size="xs"
                                        variant="ghosted"
                                        aria-label={intl.formatMessage({
                                            id: 'field.calendar.assistive_msg.open_calendar',
                                            defaultMessage: 'Open calendar'
                                        })}
                                        icon={<CalendarIcon color="black" w={4} h={4} mr="-4px" />}
                                        onClick={(e) => {
                                            e.preventDefault()
                                            e.stopPropagation()

                                            const scrollY = window.scrollY

                                            flushSync(() => {
                                                setIsDatePickerMode(true)
                                                setIsDateFocused(true)
                                            })

                                            const input = dateInputRef.current

                                            if (input) {
                                                window.scrollTo(0, scrollY)

                                                input.focus({
                                                    preventScroll: true
                                                })

                                                try {
                                                    input.showPicker?.()
                                                } catch (error) {
                                                    console.log('showPicker not supported')
                                                }
                                            }
                                        }}
                                    />
                                </InputRightElement>
                            )}

                            {isMaskedDate && isLeftIcon && (
                                <InputLeftElement
                                    w="auto"
                                    h="100%"
                                    left="8px"
                                >
                                    <IconButton
                                        size="xs"
                                        variant="ghosted"
                                        aria-label={intl.formatMessage({
                                            id: 'field.calendar.assistive_msg.open_calendar',
                                            defaultMessage: 'Open calendar'
                                        })}
                                        icon={<CalendarIcon color="black" w={4} h={4} />}
                                        onClick={(e) => {
                                            e.preventDefault()
                                            e.stopPropagation()

                                            const scrollY = window.scrollY

                                            flushSync(() => {
                                                setIsDatePickerMode(true)
                                                setIsDateFocused(true)
                                            })

                                            const input = dateInputRef.current

                                            if (input) {
                                                window.scrollTo(0, scrollY)

                                                input.focus({
                                                    preventScroll: true
                                                })

                                                try {
                                                    input.showPicker?.()
                                                } catch (error) {
                                                    console.log('showPicker not supported')
                                                }
                                            }
                                        }}
                                    />
                                </InputLeftElement>
                            )}

                            {type === 'select' && (
                                <Select
                                    ref={ref}
                                    onChange={onChange}
                                    value={value || ''}
                                    placeholder={finalPlaceholder}
                                    autoComplete={autoComplete}
                                    {...(!isWebVariant && {borderColor: 'gray.700'})}
                                    {..._inputProps}
                                    {...(isWebVariant && {
                                        variant: 'webSelect',
                                        icon: (
                                            <ChevronDownType2
                                                sx={{
                                                    transform: isSelectFocused
                                                        ? 'rotate(180deg)'
                                                        : 'rotate(0deg)',
                                                    transition: 'transform 0.2s ease-in-out'
                                                }}
                                            />
                                        ),
                                        onClick: () => {
                                            if (!selectChangedRef.current)
                                                setIsSelectFocused((prev) => !prev)
                                            selectChangedRef.current = false
                                        },
                                        onKeyDown: (e) => {
                                            if (e.key === 'Escape') setIsSelectFocused(false)
                                        },
                                        onBlur: () => setIsSelectFocused(false),
                                        onChange: (e) => {
                                            onChange(e)
                                            selectChangedRef.current = true
                                            setIsSelectFocused(false)
                                        }
                                    })}
                                >
                                    {options.map((opt) => (
                                        <option key={`${opt.label}-${opt.value}`} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </Select>
                            )}

                            {type === 'radio' && (
                                <RadioGroup
                                    ref={ref}
                                    onChange={onChange}
                                    value={value || ''}
                                    name={name}
                                    {..._inputProps}
                                >
                                    <VStack align="start" spacing={3} {...radioGroupProps}>
                                        {options.map((opt) => (
                                            <Radio
                                                key={`${opt.label}-${opt.value}`}
                                                value={opt.value}
                                                size="lg"
                                            >
                                                <Text ml={2} fontSize="md" {...labelProps}>
                                                    {opt.label}
                                                </Text>
                                            </Radio>
                                        ))}
                                    </VStack>
                                </RadioGroup>
                            )}

                            {type === 'checkbox' && (
                                <Checkbox
                                    fontSize="5px"
                                    ref={ref}
                                    onChange={(e) => onChange(e.target.checked)}
                                    isChecked={value}
                                    {..._inputProps}
                                    sx={{
                                        '.chakra-checkbox__control[data-invalid]': {
                                            borderColor: 'black'
                                        },
                                        ..._inputProps?.sx
                                    }}
                                >
                                    <Text {...labelProps} as={'span'}>
                                        {formLabel || label}
                                    </Text>
                                </Checkbox>
                            )}

                            {type === 'switch' && (
                                <Flex align="center" justify="space-between">
                                    <Flex align="center" gap="8px">
                                        <Switch
                                            ref={ref}
                                            isChecked={!!value}
                                            onChange={(e) => onChange(e.target.checked)}
                                            {..._inputProps}
                                            {...switchProps}
                                        />

                                        <Text
                                            fontSize="sm"
                                            fontWeight="400"
                                            color="black"
                                            {...labelProps}
                                        >
                                            {value ? switchLabels?.on : switchLabels?.off}
                                        </Text>
                                    </Flex>
                                </Flex>
                            )}

                            {type === 'textarea' && (
                                <Box width="100%">
                                    <Textarea
                                        ref={ref}
                                        onChange={onChange}
                                        value={value || ''}
                                        placeholder={webPlaceholder}
                                        rows={rows}
                                        maxLength={maxLength}
                                        {...(isWebVariant
                                            ? {variant: 'webTextArea'}
                                            : {
                                                  border: 'none',
                                                  borderBottom: '1px solid',
                                                  borderBottomColor: 'gray.500',
                                                  borderRadius: '0',
                                                  _focusVisible: {
                                                      boxShadow: 'none',
                                                      borderColor: 'gray.500'
                                                  },
                                                  resize: 'none',
                                                  px: 0
                                              })}
                                        {..._inputProps}
                                    />
                                    {showCharacterCount && maxLength && (
                                        <Flex justify="flex-start" mt={1}>
                                            <Text
                                                fontSize="xs"
                                                color={
                                                    value?.length >= maxLength
                                                        ? 'dangerText'
                                                        : 'black'
                                                }
                                            >
                                                {value?.length || 0}/{maxLength}
                                            </Text>
                                        </Flex>
                                    )}
                                </Box>
                            )}

                            {children}
                        </InputGroup>

                        {(variant === 'floating' ||
                            variant === 'floatingProfileForm' ||
                            isWebVariant) &&
                            labelElement}

                        {error && !hideErrorMessage && type !== 'hidden' && (
                            <FormErrorMessage
                                ml={type === 'checkbox' ? '27px' : '0'}
                                {...(isWebVariant
                                    ? {
                                          ...(theme?.textStyles?.bodyXsRegular || {}),
                                          color: 'webTypo.textError',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '4px'
                                      }
                                    : {
                                          color: 'dangerText',
                                          fontSize: isResetPassword ? 'sm' : 'xs',
                                          lineHeight: isResetPassword ? '18px' : '15px'
                                      })}
                                {...errorMessageProps}
                            >
                                {isWebVariant && (
                                    <ErrorIcon
                                        w="13px"
                                        h="13px"
                                        color="webTypo.textError"
                                        flexShrink={0}
                                    />
                                )}
                                {error.message}
                            </FormErrorMessage>
                        )}

                        {isWebVariant && webHelperText && !error && (
                            <Flex
                                mt="8px"
                                align="center"
                                gap="4px"
                                color="webTypo.textSecondary"
                                {...(theme?.textStyles?.bodyXsRegular || {})}
                                {...webHelperStyle}
                            >
                                {WebHelperIcon && (
                                    <WebHelperIcon w="16px" h="16px" flexShrink={0} />
                                )}
                                {webHelperText}
                            </Flex>
                        )}

                        {helpText}
                    </FormControl>
                )
            }}
        />
    )
}

Field.propTypes = {
    name: PropTypes.string,
    label: PropTypes.node,
    autoComplete: PropTypes.string,
    formLabel: PropTypes.any,
    type: PropTypes.oneOf([
        'text',
        'number',
        'password',
        'email',
        'phone',
        'tel',
        'select',
        'checkbox',
        'radio',
        'hidden',
        'textarea',
        'switch',
        'date'
    ]),
    options: PropTypes.arrayOf(PropTypes.shape({label: PropTypes.string, value: PropTypes.any})),
    rules: PropTypes.object,
    error: PropTypes.shape({message: PropTypes.string}),
    placeholder: PropTypes.string,
    inputProps: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    labelProps: PropTypes.object,
    control: PropTypes.object,
    defaultValue: PropTypes.any,
    helpText: PropTypes.any,
    children: PropTypes.any,
    inputRef: PropTypes.object,
    hideLabel: PropTypes.bool,
    formControlProps: PropTypes.object,
    variant: PropTypes.string,
    rows: PropTypes.number,
    maxLength: PropTypes.number,
    showCharacterCount: PropTypes.bool,
    showPasswordToggle: PropTypes.bool,
    onClick: PropTypes.func,
    isResetPassword: PropTypes.bool,
    isLoginPage: PropTypes.bool,
    secondaryLabel: PropTypes.string,
    webHelperText: PropTypes.string,
    webHelperIcon: PropTypes.elementType,
    webHelperStyle: PropTypes.object,
    hideRequiredIndicator: PropTypes.bool,
    hideErrorMessage: PropTypes.bool,
    errorMessageProps: PropTypes.object,
    showSelectedCheckmark: PropTypes.bool,
    nativeSelect: PropTypes.bool,
    dataPrivate: PropTypes.bool,
    webTooltip: PropTypes.node,
    webTooltipPlacement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
    webTooltipButtonProps: PropTypes.object
}

export default Field
